import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  Upload,
  LayoutDashboard,
  Users,
  FolderOpen,
  FileText,
  Settings,
  Terminal,
  LogOut,
  ChevronRight,
  Menu,
  X
} from 'lucide-react';

const AdminSidebar = () => {
  const { user, logout, branding } = useAuth();
  const location = useLocation();
  const [logoError, setLogoError] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const logoUrl = branding?.logo_url && !logoError
    ? `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}`
    : null;

  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/users', label: 'Users', icon: Users },
    { path: '/admin/folders', label: 'Folders', icon: FolderOpen },
    { path: '/admin/logs', label: 'Activity Logs', icon: FileText },
    { path: '/admin/system-logs', label: 'System Logs', icon: Terminal },
    { path: '/admin/settings', label: 'Settings', icon: Settings }
  ];

  const isActive = (path) => location.pathname === path;

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-zinc-950 border-b border-zinc-800 px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {logoUrl ? (
            <img 
              src={logoUrl} 
              alt="Logo" 
              className="h-8 w-auto object-contain"
              onError={() => setLogoError(true)}
            />
          ) : (
            <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
              <Upload className="w-4 h-4 text-white" />
            </div>
          )}
          <span className="text-sm text-zinc-400 uppercase tracking-wider font-bold">Admin</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-zinc-400 hover:text-white"
          data-testid="mobile-menu-toggle"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Desktop: always visible, Mobile: slide-in */}
      <aside 
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-zinc-950 border-r border-zinc-800 flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          lg:transform-none
        `}
        data-testid="admin-sidebar"
      >
        {/* Logo - Hidden on mobile (shown in mobile header instead) */}
        <div className="hidden lg:block p-6 border-b border-zinc-800">
          <Link to="/admin" className="flex flex-col items-center justify-center text-center">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt="Logo" 
                className="h-16 w-auto object-contain mb-2"
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className="w-14 h-14 bg-red-600 rounded-lg flex items-center justify-center mb-2">
                <Upload className="w-7 h-7 text-white" />
              </div>
            )}
            <span className="text-sm text-zinc-400 uppercase tracking-wider font-bold">Admin Panel</span>
          </Link>
        </div>

        {/* Mobile: Spacer for fixed header */}
        <div className="lg:hidden h-14" />

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              onClick={handleNavClick}
              className={`flex items-center gap-3 px-4 py-3 text-sm transition-all rounded-lg ${
                isActive(item.path)
                  ? 'bg-red-600/10 text-red-500 border-l-2 border-red-600'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
              }`}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
              {isActive(item.path) && <ChevronRight className="w-4 h-4 ml-auto" />}
            </Link>
          ))}
        </nav>

        {/* Upload Portal Link */}
        <div className="p-4 border-t border-zinc-800">
          <Link
            to="/upload"
            onClick={handleNavClick}
            className="flex items-center gap-3 px-4 py-3 text-sm text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-all rounded-lg"
            data-testid="nav-upload-portal"
          >
            <Upload className="w-5 h-5" />
            <span>Upload Portal</span>
          </Link>
        </div>

        {/* User Section */}
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-8 h-8 bg-zinc-800 rounded flex items-center justify-center">
              <span className="text-white text-sm font-medium">
                {user?.username?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.username}</p>
              <p className="text-zinc-500 text-xs uppercase">{user?.role}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { logout(); handleNavClick(); }}
            className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-800"
            data-testid="sidebar-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;
